import { useEffect, useState } from 'react';
import { getListPrice } from 'src/common/hooks/widgets/fetchData';
import lngToHtmlLang from '../lib/lngToHtmlLang';

export interface PropsFunc {
  localeCode: string;
  currency: string;
  country: string;
}

export interface priceProps {
  partNumber: string;
  pricingIndicator: string;
  pricingModel: number;
  scaleQtyPrice: [
    {
      qty: number;
      price: number;
    },
  ];
}

const partsToQuery = [
  {
    partNumber: 'D1P3WLL',
  },
  {
    partNumber: 'D1P3LLL',
  },
  {
    partNumber: 'D1P3RLL',
  },
  {
    partNumber: 'D1P3GLL',
  },
];

type MarketPlaceRequest = {
  currency: string;
  countryCode3: string;
  parts: typeof partsToQuery;
};

export const usePrice = ({ localeCode, currency, country }: PropsFunc) => {
  const [enterprisePrice, setEnterprisePrice] = useState(0);
  const [deluxePrice, setDeluxePrice] = useState(0);
  const [premierPrice, setPremierPrice] = useState(0);
  const [essentialPrice, setEssentialPrice] = useState(0);
  const [dataFetched, setDataFetched] = useState([]);
  const [priceFile, setPriceFile] = useState({} as priceProps[]);
  const [loading, setLoading] = useState(false);
  const [fetchingError, setFetchingError] = useState({});

  useEffect(() => {
    if (currency && dataFetched.length === 0) {
      fetchPrices();
    }
  }, [localeCode, currency, open]);

  const fetchPrices = () => {
    setLoading(true);
    const body: MarketPlaceRequest = {
      currency: currency,
      countryCode3: country,
      parts: partsToQuery,
    };
    getListPrice(
      'https://api.marketplace.ibm.com/purchase/catalog/resources/price/info',
      JSON.stringify(body),
      'POST',
    )
      .then((data) => {
        if (data.parts && data.parts.length !== 0) {
          setPriceFile(data.parts);
          setDataFetched(data.parts);
          setLoading(false);
        } else {
          setDataFetched([]);
          setLoading(false);
        }
      })
      .catch((error) => {
        setDataFetched([]);
        setLoading(false);
        if (error.response) {
          setFetchingError(error.response);
        }
      });
  };

  useEffect(() => {
    if (priceFile.length) {
      const priceArray = priceFile.map((item) => ({
        [item.partNumber]: item.scaleQtyPrice[0].price,
      }));
      const priceObj = Object.assign({}, ...priceArray);
      setEnterprisePrice(priceObj.D1P3WLL);
      setDeluxePrice(priceObj.D1P3LLL);
      setPremierPrice(priceObj.D1P3RLL);
      setEssentialPrice(priceObj.D1P3GLL);
    }
  }, [priceFile]);

  const formatPrice = (
    price: number,
    numberFormat: Intl.NumberFormat,
    currency: string,
  ) => {
    const formattedValue = numberFormat.formatToParts(price);
    return formattedValue
      .map(({ type, value }) => (type === 'currency' ? '' : value))
      .join('')
      .split('')
      .concat([` ${currency}`])
      .reduce((part, string) => part + string);
  };

  const numberFormat = new Intl.NumberFormat(lngToHtmlLang(currency), {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
    minimumFractionDigits: 2,
  });

  const formattedValueEnterprise = formatPrice(
    enterprisePrice,
    numberFormat,
    currency,
  );

  const formattedValuePremier = formatPrice(
    premierPrice,
    numberFormat,
    currency,
  );

  const formattedValueEssentials = formatPrice(
    essentialPrice,
    numberFormat,
    currency,
  );

  const formattedValueDelux = formatPrice(deluxePrice, numberFormat, currency);

  return [
    formattedValueEnterprise,
    formattedValueDelux,
    formattedValuePremier,
    formattedValueEssentials,
    loading,
    fetchingError,
  ];
};

export default usePrice;
